import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Image } from '@tiptap/extension-image';
import { useEffect, useRef } from 'react';

// PROP-IMG (2026-06-09): redimensiona a imagem no browser antes de embutir
// como data URL base64 no HTML da proposta. Mantém o documento autocontido
// (funciona no editor, no link público e no PDF via Puppeteer, sem depender
// de URL externa que expira). Max 1400px de largura, JPEG q0.85 — foto de
// celular de 4MB vira ~150-300KB. PNG pequeno (logo com transparência)
// passa sem conversão.
async function redimensionarImagem(file: File): Promise<string> {
  const MAX_LARGURA = 1400;
  const dataUrl: string = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  // PNG pequeno (provável logo/ícone com transparência): embute direto
  if (file.type === 'image/png' && file.size < 400 * 1024) return dataUrl;
  const img: HTMLImageElement = await new Promise((res, rej) => {
    const i = document.createElement('img');
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const escala = Math.min(1, MAX_LARGURA / img.naturalWidth);
  const w = Math.round(img.naturalWidth * escala);
  const h = Math.round(img.naturalHeight * escala);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  // Fundo branco pra JPEG (transparência viraria preto)
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.85);
}

// Variáveis de template — botões clicáveis na toolbar para inserir no editor.
const VARIAVEIS_TEMPLATE = [
  { grupo: 'Cliente', vars: [
    'cliente.razao_social', 'cliente.cnpj',
    'contato.nome', 'contato.email', 'contato.telefone', 'contato.cargo',
  ]},
  { grupo: 'Negócio', vars: [
    'negocio.titulo', 'negocio.area', 'negocio.prazo_execucao', 'negocio.observacoes',
  ]},
  { grupo: 'Vendedor', vars: [
    'vendedor.nome', 'vendedor.email', 'vendedor.telefone',
  ]},
  { grupo: 'Composição', vars: [
    'composicao.total_venda', 'composicao.itens_tabela', 'composicao.observacoes',
  ]},
  { grupo: 'Data', vars: [
    'data.hoje_extenso', 'data.validade',
  ]},
];

// EXPEDIENTES (2026-08-13): o vocabulário de variáveis passou a ser injetável.
// O default é o de proposta, então os 14 call-sites existentes não mudam —
// só quem passa `variaveis` (ofício/declaração) vê outro conjunto de chips.
export type GrupoVariaveis = { grupo: string; vars: string[] };

export default function RichTextEditor({ value, onChange, mostrarVariaveis = false, height = 400, variaveis }: {
  value: string;
  onChange: (html: string) => void;
  mostrarVariaveis?: boolean;
  height?: number;
  variaveis?: GrupoVariaveis[];
}) {
  const gruposVariaveis = variaveis && variaveis.length ? variaveis : VARIAVEIS_TEMPLATE;
  const inputImagemRef = useRef<HTMLInputElement>(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false }),
      // resizable: true permite arrastar o limite entre colunas pra ajustar largura.
      // Combinado com a toolbar contextual abaixo, o usuário pode editar tabelas
      // já inseridas (add/remove linhas e colunas, redimensionar).
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      // PROP-IMG: imagens embutidas como base64 (autocontido no HTML)
      Image.configure({ allowBase64: true, inline: false }),
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sincroniza quando o valor externo muda (ex.: troca de modelo)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '<p></p>', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return <div style={{ padding: 12, color: '#888' }}>Carregando editor...</div>;

  function inserirVariavel(v: string) {
    editor?.chain().focus().insertContent(`{{${v}}}`).run();
  }
  function inserirLink() {
    const url = window.prompt('URL:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  }
  // PROP-IMG: abre o seletor de arquivo, redimensiona e insere a imagem
  async function inserirImagem(file: File) {
    if (!file.type.startsWith('image/')) {
      window.alert('Selecione um arquivo de imagem (PNG, JPG, WebP…)');
      return;
    }
    try {
      const src = await redimensionarImagem(file);
      editor?.chain().focus().setImage({ src }).run();
    } catch {
      window.alert('Falha ao processar a imagem');
    }
  }

  const btnStyle = (ativo: boolean): React.CSSProperties => ({
    padding: '4px 8px', fontSize: 11, borderRadius: 3,
    border: `0.5px solid ${ativo ? '#0930D0' : '#ddd'}`,
    background: ativo ? '#E7ECFD' : '#fff',
    color: ativo ? '#0930D0' : '#444',
    cursor: 'pointer', fontWeight: ativo ? 600 : 400,
  });

  return (
    <div style={{ border: '0.5px solid #ddd', borderRadius: 5, background: '#fff', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6,
        borderBottom: '0.5px solid #e0e0e0', background: '#FAFBFF',
      }}>
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()}
          style={btnStyle(editor.isActive('bold'))} title="Negrito (Ctrl+B)"><strong>B</strong></button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()}
          style={btnStyle(editor.isActive('italic'))} title="Itálico (Ctrl+I)"><em>I</em></button>
        <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()}
          style={btnStyle(editor.isActive('strike'))} title="Riscado"><s>S</s></button>
        <span style={separador} />
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          style={btnStyle(editor.isActive('heading', { level: 1 }))}>H1</button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          style={btnStyle(editor.isActive('heading', { level: 2 }))}>H2</button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          style={btnStyle(editor.isActive('heading', { level: 3 }))}>H3</button>
        <button type="button" onClick={() => editor.chain().focus().setParagraph().run()}
          style={btnStyle(editor.isActive('paragraph'))}>P</button>
        <span style={separador} />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}
          style={btnStyle(editor.isActive('bulletList'))} title="Lista">• Lista</button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()}
          style={btnStyle(editor.isActive('orderedList'))} title="Numerada">1. Lista</button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()}
          style={btnStyle(editor.isActive('blockquote'))} title="Citação">❝</button>
        <span style={separador} />
        <button type="button" onClick={inserirLink}
          style={btnStyle(editor.isActive('link'))} title="Inserir link">🔗</button>
        <button type="button"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          style={btnStyle(false)} title="Inserir tabela 3×3">⊞ Tabela</button>
        <button type="button" onClick={() => inputImagemRef.current?.click()}
          style={btnStyle(false)} title="Inserir imagem (PNG, JPG…)">🖼️ Imagem</button>
        <span style={separador} />
        <button type="button" onClick={() => editor.chain().focus().undo().run()}
          style={btnStyle(false)} title="Desfazer (Ctrl+Z)">↶</button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()}
          style={btnStyle(false)} title="Refazer (Ctrl+Y)">↷</button>
      </div>

      {/* Toolbar contextual de tabela — aparece quando o cursor está dentro de uma table */}
      {editor.isActive('table') && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6,
          borderBottom: '0.5px solid #e0e0e0', background: '#FFF8E8',
        }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: '#B07500',
            alignSelf: 'center', marginRight: 4 }}>📋 Tabela:</span>
          <button type="button" onClick={() => editor.chain().focus().addColumnBefore().run()}
            style={btnStyle(false)} title="Adicionar coluna antes">⇤+ col</button>
          <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()}
            style={btnStyle(false)} title="Adicionar coluna depois">+col ⇥</button>
          <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()}
            style={btnStyle(false)} title="Remover coluna atual">− coluna</button>
          <span style={separador} />
          <button type="button" onClick={() => editor.chain().focus().addRowBefore().run()}
            style={btnStyle(false)} title="Adicionar linha acima">↑+ linha</button>
          <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()}
            style={btnStyle(false)} title="Adicionar linha abaixo">+ linha ↓</button>
          <button type="button" onClick={() => editor.chain().focus().deleteRow().run()}
            style={btnStyle(false)} title="Remover linha atual">− linha</button>
          <span style={separador} />
          <button type="button" onClick={() => editor.chain().focus().toggleHeaderRow().run()}
            style={btnStyle(false)} title="Marcar/desmarcar 1ª linha como cabeçalho">⤒ cabeçalho</button>
          <button type="button" onClick={() => editor.chain().focus().mergeOrSplit().run()}
            style={btnStyle(false)} title="Mesclar/dividir células selecionadas">⇆ mesclar/dividir</button>
          <span style={separador} />
          <button type="button"
            onClick={() => {
              if (window.confirm('Remover esta tabela?')) {
                editor.chain().focus().deleteTable().run();
              }
            }}
            style={{ ...btnStyle(false), color: '#B91626', borderColor: '#B91626' }}
            title="Remover tabela inteira">× remover</button>
        </div>
      )}

      {/* Variáveis (só mostra ao editar template) */}
      {mostrarVariaveis && (
        <div style={{
          padding: '6px 8px', borderBottom: '0.5px solid #e0e0e0',
          background: '#FFF8E8', fontSize: 10,
        }}>
          <strong style={{ color: '#B07500', marginRight: 6 }}>Variáveis:</strong>
          {gruposVariaveis.map(g => (
            <span key={g.grupo} style={{ marginRight: 8 }}>
              <span style={{ color: '#888', marginRight: 3 }}>{g.grupo}:</span>
              {g.vars.map(v => (
                <span key={v} onClick={() => inserirVariavel(v)}
                  style={{
                    display: 'inline-block', padding: '1px 5px', margin: '1px 2px',
                    background: '#fff', color: '#0930D0', borderRadius: 3,
                    cursor: 'pointer', fontFamily: 'monospace', fontSize: 10,
                    border: '0.5px solid #CDD4EA',
                  }}
                  title={`Inserir {{${v}}}`}>{v.split('.')[1]}</span>
              ))}
            </span>
          ))}
        </div>
      )}

      {/* PROP-IMG: input de arquivo escondido pro botão 🖼️ Imagem */}
      <input ref={inputImagemRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) inserirImagem(f);
          e.target.value = '';  // permite re-selecionar o mesmo arquivo
        }} />

      {/* Editor */}
      <EditorContent editor={editor} className="rte-editor"
        style={{ minHeight: height, padding: 12, fontSize: 13, lineHeight: 1.5 }} />
      <style>{`
        .rte-editor .ProseMirror { outline: none; min-height: ${height - 24}px; }
        .rte-editor .ProseMirror h1 { font-size: 22px; color: #020D7F; margin: 16px 0 8px; }
        .rte-editor .ProseMirror h2 { font-size: 17px; color: #020D7F; margin: 14px 0 6px; }
        .rte-editor .ProseMirror h3 { font-size: 14px; color: #020D7F; margin: 10px 0 4px; }
        .rte-editor .ProseMirror p { margin: 4px 0; }
        .rte-editor .ProseMirror table { border-collapse: collapse; margin: 8px 0; width: 100%; table-layout: fixed; overflow: hidden; }
        .rte-editor .ProseMirror th, .rte-editor .ProseMirror td { border: 0.5px solid #ddd; padding: 5px 8px; position: relative; vertical-align: top; box-sizing: border-box; min-width: 1em; }
        .rte-editor .ProseMirror th { background: #020D7F; color: #fff; }
        .rte-editor .ProseMirror a { color: #0930D0; text-decoration: underline; }
        .rte-editor .ProseMirror blockquote { border-left: 3px solid #ddd; padding-left: 12px; color: #666; }
        .rte-editor .ProseMirror ul, .rte-editor .ProseMirror ol { padding-left: 24px; }
        /* PROP-IMG: imagens nunca estouram a largura; selecionada ganha contorno */
        .rte-editor .ProseMirror img { max-width: 100%; height: auto; display: block; margin: 8px 0; }
        .rte-editor .ProseMirror img.ProseMirror-selectednode { outline: 2px solid #0930D0; }
        /* Tiptap: célula(s) selecionada(s) — feedback visual pra add/remove linha/coluna */
        .rte-editor .ProseMirror .selectedCell:after {
          z-index: 2; position: absolute; content: "";
          left: 0; right: 0; top: 0; bottom: 0;
          background: rgba(9, 48, 208, 0.15); pointer-events: none;
        }
        /* Tiptap: handle de resize de coluna */
        .rte-editor .ProseMirror .column-resize-handle {
          position: absolute; right: -2px; top: 0; bottom: -2px; width: 4px;
          background-color: #0930D0; pointer-events: none;
        }
        .rte-editor .ProseMirror.resize-cursor { cursor: ew-resize; cursor: col-resize; }
      `}</style>
    </div>
  );
}

const separador: React.CSSProperties = {
  width: 1, height: 18, background: '#ddd', margin: '0 2px', alignSelf: 'center',
};
